// =============================================================================
// FlowPersistence: Handles serialization and deserialization of flow state
// for resuming flows after page refresh
// =============================================================================

export type ExecutionPhase = 'onInit' | 'onEventSetup' | 'onExecute' | 'onReconciliationExecute' | 'onEventTearDown' | 'onExit' | 'onError' | 'onPause' | 'onResume' | 'onCancel' | 'onExitFlow';

export interface SerializableStepState {
    stepName: string;
    stepId: string;
    hasSignaledNext: boolean;
    requiresReconciliation: boolean;
    reconciliationState?: Record<string, any>;
    receivedData?: Record<string, any>;
    context?: Record<string, any>;
    executionPhase: ExecutionPhase;
    timestamp: string;
}

export interface SerializableFlowState {
    flowId: string;
    flowName: string;
    state: "running" | "paused" | "cancelled" | "exited";
    currentSteps: SerializableStepState[];
    contextRepository: Record<string, Record<string, any>>;
    executionLogs: Array<{
        flowId: string;
        stepName: string;
        method: string;
        stepId: string;
        timestamp: string;
    }>;
    startStep?: string;
    timestamp: string;
}

export class FlowPersistence {
    private static readonly STORAGE_KEY_PREFIX = 'flow_state_';
    private static readonly MAX_AGE = 60 * 5 * 1000; // 5 minutes

    // Save flow state to localStorage
    static saveFlowState(flowId: string, flowState: SerializableFlowState): void {
        try {
            const storageKey = `${this.STORAGE_KEY_PREFIX}${flowState.flowName}`;
            flowState.timestamp = new Date().toISOString();
            localStorage.setItem(storageKey, JSON.stringify(flowState));
        } catch (error) {
            console.warn(`[FlowPersistence] Failed to save flow state for ${flowState.flowName}:`, error);
        }
    }

    // Load flow state from localStorage
    static loadFlowState(flowName: string): SerializableFlowState | null {
        try {
            const storageKey = `${this.STORAGE_KEY_PREFIX}${flowName}`;
            const stored = localStorage.getItem(storageKey);

            if (!stored) return null;

            const flowState: SerializableFlowState = JSON.parse(stored);

            // Check if state is too old
            const stateAge = Date.now() - new Date(flowState.timestamp).getTime();
            if (stateAge > this.MAX_AGE) {
                this.clearFlowState(flowName);
                return null;
            }

            return flowState;
        } catch (error) {
            console.warn(`[FlowPersistence] Failed to load flow state for ${flowName}:`, error);
            this.clearFlowState(flowName);
            return null;
        }
    }

    // Clear flow state from localStorage
    static clearFlowState(flowName: string): void {
        try {
            const storageKey = `${this.STORAGE_KEY_PREFIX}${flowName}`;
            localStorage.removeItem(storageKey);
        } catch (error) {
            console.warn(`[FlowPersistence] Failed to clear flow state for ${flowName}:`, error);
        }
    }

    // Check if a flow has persisted state
    static hasPersistedState(flowName: string): boolean {
        try {
            const storageKey = `${this.STORAGE_KEY_PREFIX}${flowName}`;
            return localStorage.getItem(storageKey) !== null;
        } catch (error) {
            return false;
        }
    }

    // Clean up old or invalid flow states
    static cleanup(): void {
        try {
            // Get all flow keys from localStorage
            const flowKeys = Object.keys(localStorage).filter(key => key.startsWith(this.STORAGE_KEY_PREFIX));
            const now = Date.now();

            flowKeys.forEach(key => {
                try {
                    const stored = localStorage.getItem(key);
                    if (!stored) return;

                    const flowState = JSON.parse(stored);
                    const stateAge = now - new Date(flowState.timestamp).getTime();

                    //if (stateAge > this.MAX_AGE || flowState.state === 'cancelled' || flowState.state === 'exited') {
                    localStorage.removeItem(key);
                    //}
                } catch (error) {
                    // Remove corrupted state
                    localStorage.removeItem(key);
                }
            });
        } catch (error) {
            console.warn('[FlowPersistence] Cleanup failed:', error);
        }
    }

    // Serialize context data safely
    static serializeContext(context: any): Record<string, any> {
        try {
            // Handle circular references and non-serializable objects
            const seen = new WeakSet();

            return JSON.parse(JSON.stringify(context, (key, value) => {
                if (typeof value === 'object' && value !== null) {
                    if (seen.has(value)) {
                        return '[Circular]';
                    }
                    seen.add(value);
                }

                // Handle functions, undefined, symbols
                if (typeof value === 'function') return '[Function]';
                if (value === undefined) return '[Undefined]';
                if (typeof value === 'symbol') return '[Symbol]';

                return value;
            }));
        } catch (error) {
            console.warn('[FlowPersistence] Context serialization failed:', error);
            return {};
        }
    }

    // Deserialize context data
    static deserializeContext(serializedContext: Record<string, any>): any {
        try {
            // Create a new Map for ContextFlow if needed
            if (serializedContext && typeof serializedContext === 'object') {
                return serializedContext;
            }
            return {};
        } catch (error) {
            console.warn('[FlowPersistence] Context deserialization failed:', error);
            return {};
        }
    }
}